import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  X, Upload, Loader2, FileText, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Trash2, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useSuppliers, useActiveContractGroups, queryKeys } from '../../../hooks/queries';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { contractService } from '../../../services/api/contractService';
import { categoryLabel } from '../../../utils/contractCategories';
import { RepositoryContractForm, validateForm, toNewRepositoryContract } from './RepositoryContractForm';
import { SignatureNotice } from './signature';
import {
  batchReducer,
  BatchRow,
  getRowIssues,
  isRowReady,
  summarise,
  ISSUE_LABELS,
  MAX_BATCH_FILES,
  withRateLimitRetry
} from './batchImport';
import { ACCEPTED_EXTENSIONS, checkContractFile } from './fileChecks';

interface BatchImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after contracts are saved, so other views can refresh. */
  onAdded?: () => void;
}

// Documents read and saved at the same time. Enough to keep things moving
// without tripping the AI service's rate limits.
const CONCURRENCY = 3;

type Filter = 'all' | 'attention' | 'ready' | 'saved';

const primaryButton =
  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 dark:text-gray-900 rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButton =
  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50';

const formatDate = (value: string) =>
  value
    ? new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '';

/** Run `worker` over `items`, at most `limit` at a time. */
const runPool = async <T,>(items: T[], limit: number, worker: (item: T) => Promise<void>) => {
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        const item = items[next++];
        await worker(item);
      }
    })
  );
};

/**
 * Import many signed contracts at once. Each document is read by AI, the
 * results land in a table to check and correct, and every row that's ready
 * is saved in one go. Rows with problems stay behind until they're fixed.
 */
export const BatchImportModal: React.FC<BatchImportModalProps> = ({ isOpen, onClose, onAdded }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [rows, dispatch] = useReducer(batchReducer, []);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [skipped, setSkipped] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Rows already sent to be read, so a re-render never reads one twice.
  const startedRef = useRef(new Set<string>());

  const { data: suppliers = [] } = useSuppliers({ enabled: isOpen });
  const { data: groups = [] } = useActiveContractGroups({ enabled: isOpen });
  const supplierName = useMemo(() => {
    const byId = new Map(suppliers.map((s) => [String(s.id), s.name]));
    return (id: string) => byId.get(id) ?? '';
  }, [suppliers]);

  const summary = summarise(rows);
  const unsaved = rows.length - summary.saved;
  useUnsavedChanges({
    hasUnsavedChanges: isOpen && unsaved > 0,
    message: 'Some imported contracts have not been saved yet. Leave anyway?'
  });

  // Keep up to CONCURRENCY documents being read at once.
  useEffect(() => {
    if (!token) return;
    const reading = rows.filter((r) => r.status === 'reading').length;
    const waiting = rows.filter((r) => r.status === 'queued' && !startedRef.current.has(r.id));
    waiting.slice(0, Math.max(0, CONCURRENCY - reading)).forEach((row) => {
      startedRef.current.add(row.id);
      dispatch({ type: 'startReading', id: row.id });
      withRateLimitRetry(() => contractService.extractRepositoryContract(token, row.file))
        .then((result) => dispatch({ type: 'read', id: row.id, result }))
        .catch((err: Error) => dispatch({ type: 'readFailed', id: row.id, message: err.message || 'Could not be read' }));
    });
  }, [rows, token]);

  if (!isOpen) return null;

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const accepted: File[] = [];
    const rejected: string[] = [];
    Array.from(fileList).forEach((file) => {
      const problem = checkContractFile(file);
      if (problem) rejected.push(`${file.name}: ${problem}`);
      else accepted.push(file);
    });
    const room = MAX_BATCH_FILES - rows.length;
    if (accepted.length > room) {
      accepted.slice(room).forEach((f) => rejected.push(`${f.name}: a batch holds up to ${MAX_BATCH_FILES} files`));
    }
    setSkipped(rejected);
    dispatch({ type: 'add', files: accepted.slice(0, Math.max(0, room)) });
  };

  const saveReady = async () => {
    if (!token) return;
    const ready = rows.filter((r) => isRowReady(r, rows));
    if (ready.length === 0) return;
    setIsSaving(true);
    setExpandedId(null);
    let savedAny = false;
    await runPool(ready, CONCURRENCY, async (row) => {
      dispatch({ type: 'startSaving', id: row.id });
      try {
        if (row.attachTo) {
          const metadata: Record<string, string> = { versionType: 'signed' };
          if (row.form.versionLabel.trim()) metadata.versionLabel = row.form.versionLabel.trim();
          if (row.form.notes.trim()) metadata.notes = row.form.notes.trim();
          if (row.form.signatureStatus) metadata.signatureStatus = row.form.signatureStatus;
          if (row.form.signatureNotes) metadata.signatureNotes = row.form.signatureNotes;
          const { contractId } = row.attachTo;
          await withRateLimitRetry(() => contractService.uploadFile(token, contractId, row.file, metadata));
        } else {
          const details = toNewRepositoryContract(row.form);
          await withRateLimitRetry(() => contractService.createRepositoryContract(token, row.file, details));
        }
        dispatch({ type: 'saved', id: row.id });
        savedAny = true;
      } catch (err) {
        dispatch({ type: 'saveFailed', id: row.id, message: err instanceof Error ? err.message : 'Saving failed' });
      }
    });
    setIsSaving(false);
    if (savedAny) {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      onAdded?.();
    }
  };

  const busy = isSaving || summary.saving > 0;

  const close = () => {
    if (busy) return;
    if (unsaved > 0 && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    dispatch({ type: 'reset' });
    startedRef.current.clear();
    setExpandedId(null);
    setFilter('all');
    setSkipped([]);
    setConfirmDiscard(false);
    onClose();
  };

  const needsAttention = (row: BatchRow) => row.status === 'review' && !isRowReady(row, rows);
  const visibleRows = rows.filter((row) => {
    if (filter === 'attention') return needsAttention(row);
    if (filter === 'ready') return isRowReady(row, rows);
    if (filter === 'saved') return row.status === 'saved';
    return true;
  });

  const statusCell = (row: BatchRow) => {
    if (row.status === 'queued') return <span className="text-gray-500 dark:text-gray-400">Waiting…</span>;
    if (row.status === 'reading') {
      return (
        <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Reading…
        </span>
      );
    }
    if (row.status === 'saving') {
      return (
        <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Saving…
        </span>
      );
    }
    if (row.status === 'saved') {
      return (
        <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Saved
        </span>
      );
    }
    if (row.saveError) {
      return (
        <span className="inline-flex items-center gap-1.5 text-rose-700 dark:text-rose-400">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden /> {row.saveError}
        </span>
      );
    }
    if (isRowReady(row, rows)) {
      return (
        <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Ready
        </span>
      );
    }
    const { blocking, review } = getRowIssues(row, rows);
    const first = blocking[0] ?? review[0];
    const extra = blocking.length + review.length - 1;
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        {first ? ISSUE_LABELS[first] : 'Needs review'}
        {extra > 0 && <span className="text-amber-700/70 dark:text-amber-400/70">+{extra}</span>}
      </span>
    );
  };

  const reviewPanel = (row: BatchRow) => {
    const { blocking, review } = getRowIssues(row, rows);
    const duplicate = row.extraction?.duplicate;
    return (
      <div className="space-y-4 p-4 sm:p-5 bg-gray-50 dark:bg-dark-bg-secondary border-y border-gray-100 dark:border-dark-border">
        {row.readError && (
          <p className="flex gap-2 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
            {row.readError}
          </p>
        )}
        {duplicate && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
            <span>
              {row.attachTo ? (
                <>This document will be added as a new signed version of <strong>{row.attachTo.contractTitle}</strong>.</>
              ) : (
                <><strong>{duplicate.contractTitle}</strong> already has contract number {duplicate.contractNumber}.</>
              )}
            </span>
            <button
              type="button"
              onClick={() =>
                dispatch({
                  type: 'attach',
                  id: row.id,
                  target: row.attachTo ? null : { contractId: duplicate._id, contractTitle: duplicate.contractTitle }
                })
              }
              className="flex-shrink-0 font-medium underline"
            >
              {row.attachTo ? 'Create a new contract instead' : 'Add as a new version of it'}
            </button>
          </div>
        )}
        {row.attachTo && <SignatureNotice extracted={row.extraction?.extracted ?? null} />}
        {!row.attachTo && (
          <RepositoryContractForm
            idPrefix={`batch-${row.id}`}
            values={row.form}
            onChange={(form) => dispatch({ type: 'edit', id: row.id, form })}
            errors={validateForm(row.form)}
            suppliers={suppliers}
            groups={groups}
            extracted={row.extraction?.extracted ?? null}
            supplierMatched={Boolean(row.extraction?.supplierMatch)}
          />
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              dispatch({ type: 'remove', id: row.id });
              setExpandedId(null);
            }}
            className={secondaryButton}
          >
            <Trash2 className="w-4 h-4" aria-hidden /> Leave out
          </button>
          {blocking.length === 0 && review.length > 0 && !row.confirmed ? (
            <button
              type="button"
              onClick={() => {
                dispatch({ type: 'confirm', id: row.id });
                setExpandedId(null);
              }}
              className={primaryButton}
            >
              <CheckCircle2 className="w-4 h-4" aria-hidden /> Details are correct
            </button>
          ) : (
            <button type="button" onClick={() => setExpandedId(null)} className={primaryButton}>
              Done
            </button>
          )}
        </div>
        {blocking.length > 0 && (
          <p className="text-xs text-right text-gray-500 dark:text-gray-400">
            Fix: {blocking.map((b) => ISSUE_LABELS[b].toLowerCase()).join(', ')}.
          </p>
        )}
      </div>
    );
  };

  const filterTabs: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: summary.total },
    { key: 'attention', label: 'Needs review', count: summary.needsReview },
    { key: 'ready', label: 'Ready', count: summary.ready },
    { key: 'saved', label: 'Saved', count: summary.saved }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-import-title"
        className="relative w-full max-w-6xl h-[92vh] flex flex-col bg-white dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border"
        onKeyDown={(e) => e.key === 'Escape' && close()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-dark-border">
          <div>
            <h2 id="batch-import-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Import signed contracts
            </h2>
            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
              {rows.length === 0
                ? `Add up to ${MAX_BATCH_FILES} documents. Each is read automatically, then you check the details before saving.`
                : [
                    summary.reading > 0 && `${summary.reading} reading`,
                    summary.needsReview > 0 && `${summary.needsReview} need review`,
                    summary.ready > 0 && `${summary.ready} ready`,
                    summary.saved > 0 && `${summary.saved} saved`
                  ]
                    .filter(Boolean)
                    .join(' · ')}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={busy}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
          className="sr-only"
          aria-label="Choose signed contracts"
        />

        {/* Body */}
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          {skipped.length > 0 && (
            <div role="alert" className="mx-4 sm:mx-6 mt-4 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
              <p className="font-medium">{skipped.length} file{skipped.length === 1 ? '' : 's'} skipped</p>
              <ul className="mt-1 list-disc pl-5 space-y-0.5">
                {skipped.slice(0, 5).map((s) => <li key={s}>{s}</li>)}
                {skipped.length > 5 && <li>and {skipped.length - 5} more</li>}
              </ul>
            </div>
          )}

          {rows.length === 0 ? (
            <div className="p-4 sm:p-6 h-full">
              <div
                className={`h-full min-h-[16rem] flex flex-col items-center justify-center gap-3 px-4 text-center rounded-xl border-2 border-dashed transition-colors ${
                  isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-dark-border'
                }`}
              >
                <Upload className="w-10 h-10 text-gray-400" aria-hidden />
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Drop signed contracts here or{' '}
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="font-medium text-primary-700 dark:text-accent-400 hover:underline">
                    choose files
                  </button>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">PDF and Word are read automatically. Up to 50 MB each.</p>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div role="tablist" aria-label="Show" className="inline-flex p-1 bg-gray-100 dark:bg-dark-bg-secondary rounded-lg overflow-x-auto">
                  {filterTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={filter === tab.key}
                      onClick={() => setFilter(tab.key)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                        filter === tab.key
                          ? 'bg-white dark:bg-dark-surface text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      {tab.label} <span className="tabular-nums text-gray-400 dark:text-gray-500">{tab.count}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={rows.length >= MAX_BATCH_FILES}
                  className={secondaryButton}
                >
                  <Upload className="w-4 h-4" aria-hidden /> Add more files
                </button>
              </div>

              <div className={`rounded-xl border overflow-hidden ${isDragging ? 'border-primary-500' : 'border-gray-100 dark:border-dark-border'}`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100 dark:divide-dark-border">
                    <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                      <tr>
                        {['Document', 'Supplier', 'Contract', 'Period', 'Category', 'Status'].map((h, i) => (
                          <th
                            key={h}
                            scope="col"
                            className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                              i === 4 ? 'hidden lg:table-cell' : i === 3 ? 'hidden md:table-cell' : ''
                            }`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                      {visibleRows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                            Nothing here.
                          </td>
                        </tr>
                      )}
                      {visibleRows.map((row) => {
                        const expandable = row.status === 'review';
                        const expanded = expandedId === row.id;
                        const toggle = () => expandable && setExpandedId(expanded ? null : row.id);
                        return (
                          <React.Fragment key={row.id}>
                            <tr
                              onClick={toggle}
                              className={`${expandable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg-secondary' : ''} ${
                                expanded ? 'bg-gray-50 dark:bg-dark-bg-secondary' : ''
                              } transition-colors`}
                            >
                              <td className="px-4 py-3 max-w-[16rem]">
                                <div className="flex items-center gap-2 min-w-0">
                                  {expandable ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggle();
                                      }}
                                      aria-expanded={expanded}
                                      aria-label={`${expanded ? 'Close' : 'Review'} ${row.file.name}`}
                                      className="flex-shrink-0 p-0.5 rounded text-gray-500 hover:text-gray-900 dark:hover:text-white"
                                    >
                                      {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                  ) : (
                                    <FileText className="w-4 h-4 flex-shrink-0 text-gray-400" aria-hidden />
                                  )}
                                  <span className="text-sm text-gray-900 dark:text-white truncate" title={row.file.name}>
                                    {row.file.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[12rem] truncate">
                                {row.attachTo ? '—' : supplierName(row.form.supplierId) || (row.status === 'review' ? <span className="text-gray-400">Not set</span> : '')}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[14rem] truncate">
                                {row.attachTo ? <span className="italic">New version of {row.attachTo.contractTitle}</span> : row.form.contractTitle}
                              </td>
                              <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                                {!row.attachTo && (row.form.startDate || row.form.endDate)
                                  ? `${formatDate(row.form.startDate) || '?'} – ${formatDate(row.form.endDate) || '?'}`
                                  : ''}
                              </td>
                              <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                {row.attachTo ? '' : categoryLabel(row.form.category)}
                              </td>
                              <td className="px-4 py-3 text-sm whitespace-nowrap">{statusCell(row)}</td>
                            </tr>
                            {expanded && (
                              <tr>
                                <td colSpan={6} className="p-0">
                                  {reviewPanel(row)}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-dark-border">
          {confirmDiscard ? (
            <>
              <p role="alert" className="text-sm text-gray-700 dark:text-gray-300">
                {unsaved} document{unsaved === 1 ? " hasn't" : "s haven't"} been saved. Close and discard {unsaved === 1 ? 'it' : 'them'}?
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setConfirmDiscard(false)} className={secondaryButton}>
                  Keep working
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors"
                >
                  Discard
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {summary.needsReview > 0
                  ? 'Rows that need review stay here until they’re fixed or confirmed.'
                  : summary.reading > 0
                    ? 'Keep this window open while documents are read.'
                    : ''}
              </p>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={close} disabled={busy} className={secondaryButton}>
                  {unsaved === 0 && rows.length > 0 ? 'Done' : 'Close'}
                </button>
                <button type="button" onClick={saveReady} disabled={busy || summary.ready === 0} className={primaryButton}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Upload className="w-4 h-4" aria-hidden />}
                  {busy
                    ? 'Saving…'
                    : summary.ready > 0
                      ? `Save ${summary.ready} ready contract${summary.ready === 1 ? '' : 's'}`
                      : 'Save ready contracts'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchImportModal;
